'use client';

import { FilePathUtils } from '@/config';
import { Link } from '@/i18n/routing';
import { dateUtils } from '@/utils/date-utils';
import { fallbackImages } from '@/utils/fallback-images';
import { WebinarViewModel } from '@hidayahlab/academy-suite-corelib';
import { Calendar01Icon, Clock01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Image from 'next/image';
import WebinarBadge from './webinar-badge';
import { WebinarCardAction } from './webinar-card-action';

export type WebinarCardProps = {
  webinar: WebinarViewModel;
  upcoming?: boolean;
  locale: string;
};

export default function WebinarCard({
  webinar,
  upcoming,
  locale,
}: WebinarCardProps) {
  const { classDate, classTime } = dateUtils.formatter.forClassDateAndTime(
    webinar.dateTime,
    locale,
  );

  return (
    <Link
      href={`/webinars/${webinar.id}`}
      className=" bg-white border border-gray-200 flex flex-col gap-2 justify-between cursor-pointer hover:scale-[103%] hover:shadow-lg hover:border hover:border-primary-500 transition-all duration-300 rounded-md relative overflow-hidden shadow-md"
    >
      <div>
        <div className="relative w-full aspect-[1600/960] mb-4">
          <Image
            src={
              webinar.thumbnail
                ? `${FilePathUtils.webinarPath(webinar.id)}/${webinar.thumbnail}`
                : fallbackImages.course
            }
            alt={webinar.title}
            layout="fill"
            objectFit="cover"
          />
        </div>
        {webinar.dateTime != null && (
          <div className="flex flex-col px-4 gap-2">
            <div className="flex justify-between gap-2">
              <div className="flex gap-2 items-center p-2 bg-gray-100 rounded-xs">
                <HugeiconsIcon
                  icon={Calendar01Icon}
                  className="text-gray-700 size-4"
                />
                <p className="text-sm text-gray-700">{classDate}</p>
              </div>
              <div className="flex gap-2 items-center p-2 bg-gray-100 rounded-xs">
                <HugeiconsIcon
                  icon={Clock01Icon}
                  className="text-gray-700 size-4"
                />
                <p className="text-sm text-gray-700">{classTime}</p>
              </div>
            </div>
          </div>
        )}

        <div className="p-3 grid grid-rows-[min-content_min-content_1fr] h-full gap-2">
          <h3 className="text-lg font-bold text-gray-700">{webinar.title}</h3>
          <div
            className={`flex items-center ${upcoming ? 'justify-between' : 'justify-end'}`}
          >
            {upcoming && <WebinarBadge>Upcoming</WebinarBadge>}
            <WebinarCardAction
              webinarId={webinar.id}
              courseId={webinar.courseId}
              batchId={webinar.batchId}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}
